const _excluded = ["variation", "config", "enableSearch", "hidePhoneNumber", "isCorporate", "withAbsoluteLinks", "absoluteBaseUrl"],
  _excluded2 = ["variation", "config", "enableSearch", "hidePhoneNumber", "isCorporate", "invertSmallHeader", "withAbsoluteLinks", "absoluteBaseUrl"],
  _excluded3 = ["variation", "isCorporate", "invertSmallHeader", "withAbsoluteLinks"];
function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var s = Object.getOwnPropertySymbols(e);
    for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (e.includes(n)) continue;
    t[n] = r[n];
  }
  return t;
}
import React, { useContext } from "react";
import styled, { ThemeContext } from "styled-components";
import { standardFocusStyleDeclarations } from "@nib-components/theme";
import { SearchSystemIcon } from "@nib/icons";
import { PrimaryButton } from "@nib-components/button";
import { Inline, Container, breakpoint, px, py, mr } from "@nib/layout";
import Logo from "@nib-components/logo";
import Ribbon from "./Ribbon";
import variantConfig from "../config";
import { SmallHeader } from "../SmallHeader";
import { HeaderWrapper, HeaderContainer, LogoLink, HeaderContact, PhoneNumber, PhoneIcon, HeaderLinks, SearchLink, HeaderNav, NavMenu, baseNavLinkStyles, NavLinkItem, SEARCH_URL } from "../shared";
const SmallHeaderBelowXl = styled(SmallHeader)`
  ${breakpoint("xl")`
    // Styled components has a serious issue with client-side hydration style order.
    // See issue: https://github.com/styled-components/styled-components/issues/3482
    // This is our way of compensating for unpredictable style order.
    &&& {
      display: none;
    }
  `}
`;
const HeaderButton = styled(PrimaryButton)`
  ${mr(5)};

  ${breakpoint("xl", "xxl")`
    ${mr(3)};
  `}

  &:focus-visible {
    ${standardFocusStyleDeclarations};
  }
`;
const NavLink = styled.a`
  ${baseNavLinkStyles};
  ${py(6)};
  ${px(5)};
  font-size: 18px;

  ${breakpoint("xl", "xxl")`
    font-size: 1rem;
    ${py(5)};
    ${px(3)};
  `}
`;
const PhoneNumberSpan = styled.span`
  unicode-bidi: plaintext;
`;
export const HeaderTop = props => {
  const {
      variation = "arhi",
      config,
      enableSearch = false,
      hidePhoneNumber = false,
      isCorporate,
      withAbsoluteLinks,
      absoluteBaseUrl
    } = props,
    otherProps = _objectWithoutProperties(props, _excluded);
  const dataSource = config ? config : variantConfig[variation]({
    withAbsoluteLinks,
    absoluteBaseUrl
  });
  const {
    id,
    logoUrl,
    callToAction,
    phoneNumber,
    corporatePhoneNumber,
    ribbon
  } = dataSource;
  const defaultOrCorporatePhoneNumber = isCorporate && corporatePhoneNumber ? corporatePhoneNumber : phoneNumber;
  const logoLink = logoUrl ? logoUrl : "/";
  const CustomLogo = dataSource === null || dataSource === void 0 ? void 0 : dataSource.logoComponent;
  const customLogoLabel = dataSource === null || dataSource === void 0 ? void 0 : dataSource.logoLabel;
  const themeContext = useContext(ThemeContext);
  const logoLabel = `${themeContext.id} Logo`;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderWrapper, _extends({
    id: `${id}-top`
  }, otherProps), ribbon && ribbon.length > 0 && /*#__PURE__*/React.createElement(Ribbon, {
    data: ribbon
  }), /*#__PURE__*/React.createElement(HeaderContainer, null, /*#__PURE__*/React.createElement(LogoLink, {
    href: logoLink,
    id: "ga-inlink-logo-homepage-desktop",
    "aria-label": customLogoLabel ? customLogoLabel : logoLabel
  }, CustomLogo ? /*#__PURE__*/React.createElement(CustomLogo, {
    size: "header"
  }) : /*#__PURE__*/React.createElement(Logo, {
    size: "header"
  })), defaultOrCorporatePhoneNumber && !hidePhoneNumber && /*#__PURE__*/React.createElement(HeaderContact, null, /*#__PURE__*/React.createElement(PhoneNumber, {
    href: `tel:${defaultOrCorporatePhoneNumber && defaultOrCorporatePhoneNumber.replace(/\s+/g, "")}`,
    id: `ga-nav-${variation !== "arhi" ? `${variation}-` : ""}call-desktop`,
    className: "js-phone-number-call-tracking t-header-phone-number",
    "aria-label": "Call nib"
  }, /*#__PURE__*/React.createElement(Inline, {
    space: 2
  }, /*#__PURE__*/React.createElement(PhoneIcon, null), /*#__PURE__*/React.createElement(PhoneNumberSpan, null, defaultOrCorporatePhoneNumber)))), /*#__PURE__*/React.createElement(HeaderLinks, null, enableSearch && /*#__PURE__*/React.createElement(SearchLink, {
    id: "ga-nav-search-desktop",
    href: SEARCH_URL
  }, /*#__PURE__*/React.createElement(SearchSystemIcon, null)), callToAction && /*#__PURE__*/React.createElement(HeaderButton, {
    component: "a",
    id: `${callToAction.id}-desktop`,
    href: callToAction.url
  }, callToAction.title)))));
};
export const HeaderMenu = props => {
  const {
      variation = "arhi",
      config,
      enableSearch = false,
      hidePhoneNumber = false,
      isCorporate,
      invertSmallHeader,
      withAbsoluteLinks,
      absoluteBaseUrl
    } = props,
    otherProps = _objectWithoutProperties(props, _excluded2);
  const dataSource = config ? config : variantConfig[variation]({
    withAbsoluteLinks,
    absoluteBaseUrl
  });
  const {
    id,
    navAlignment,
    navLinks
  } = dataSource;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderWrapper, _extends({
    id: `${id}-menu`
  }, otherProps, {
    as: "div"
  }), /*#__PURE__*/React.createElement(HeaderNav, {
    "aria-label": "Main navigation"
  }, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(NavMenu, {
    navAlignment: navAlignment
  }, navLinks && navLinks.map(link => /*#__PURE__*/React.createElement(NavLinkItem, {
    link: link,
    key: link.id,
    navLink: NavLink
  })))))), /*#__PURE__*/React.createElement(SmallHeaderBelowXl, {
    enableSearch: enableSearch,
    hidePhoneNumber: hidePhoneNumber,
    variation: variation,
    config: config,
    showMenu: true,
    isCorporate: isCorporate,
    invert: invertSmallHeader,
    withAbsoluteLinks: withAbsoluteLinks,
    absoluteBaseUrl: absoluteBaseUrl
  }));
};
export const Header = _ref => {
  let {
      variation = "arhi",
      isCorporate = false,
      invertSmallHeader = false,
      withAbsoluteLinks = false
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded3);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderTop, _extends({
    variation: variation,
    isCorporate: isCorporate,
    invertSmallHeader: invertSmallHeader,
    withAbsoluteLinks: withAbsoluteLinks
  }, props)), /*#__PURE__*/React.createElement(HeaderMenu, _extends({
    variation: variation,
    isCorporate: isCorporate,
    invertSmallHeader: invertSmallHeader,
    withAbsoluteLinks: withAbsoluteLinks
  }, props)));
};
Header.displayName = "Header";
Header.HeaderWrapper = HeaderWrapper;
Header.SmallHeader = SmallHeaderBelowXl;
Header.LogoLink = LogoLink;
Header.HeaderNav = HeaderNav;
Header.HeaderLinks = HeaderLinks;
Header.SearchLink = SearchLink;